/**
 * Yksittäinen tilausrivi taulukossa
 */

import { memo, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TableCell, TableRow } from "@/components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ChevronDown,
  ChevronRight,
  MoreHorizontal,
  Pencil,
  Copy,
  ExternalLink,
  X,
  Wrench,
  Clock,
  CheckCircle2,
  XCircle,
  Building2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { getCustomerDisplayName, formatCurrency } from "@/lib/formatters";
import {
  getConfigurationInfo,
  getFirstProductDescription,
  getDaysBadge,
} from "@/lib/order-helpers";
import {
  ORDER_STATUS_LABELS,
  ORDER_STATUS_COLORS,
  ORDER_TYPE_COLORS,
  FULFILLMENT_TYPE_LABELS,
  FULFILLMENT_TYPE_COLORS,
  CEMETERY_APPROVAL_STATUS_LABELS,
  CEMETERY_APPROVAL_STATUS_COLORS,
  type OrderStatus,
  type OrderType,
  type FulfillmentType,
  type CemeteryApprovalStatus,
} from "@/config/order-constants";
import { ContactButtons } from "@/components/orders/ContactButtons";
import type { OrderListItem } from "@/types/orders";
import { OrderExpandedDetails } from "./OrderExpandedDetails";

// Status transition map: current status → allowed next statuses
const STATUS_TRANSITIONS: Record<string, OrderStatus[]> = {
  draft: ["confirmed"],
  confirmed: ["ready_for_installation"],
  ready_for_installation: ["installed"],
  installed: ["completed"],
};

interface OrderTableRowProps {
  order: OrderListItem;
  isExpanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onCancel: () => void;
  onCopyLink: () => void;
  onStatusChange?: (orderId: string, newStatus: string, oldStatus: string) => void;
}

export const OrderTableRow = memo(function OrderTableRow({
  order,
  isExpanded,
  onToggle,
  onEdit,
  onCancel,
  onCopyLink,
  onStatusChange,
}: OrderTableRowProps) {
  const navigate = useNavigate();
  const dropdownJustClosed = useRef(false);

  // Memoized calculations
  const configInfo = useMemo(
    () => getConfigurationInfo(order.order_lines),
    [order.order_lines]
  );
  const productDesc = useMemo(
    () => getFirstProductDescription(order.order_lines),
    [order.order_lines]
  );
  const daysBadge = useMemo(() => getDaysBadge(order.order_date), [order.order_date]);

  const handleRowClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest("[data-no-navigate]")) return;
    if (dropdownJustClosed.current) {
      dropdownJustClosed.current = false;
      return;
    }
    navigate(`/orders/${order.id}`);
  };

  return (
    <Collapsible open={isExpanded} onOpenChange={onToggle} asChild>
      <>
        <TableRow
          className={cn(
            isExpanded && "bg-muted/50",
            "cursor-pointer hover:bg-muted/30 transition-colors"
          )}
          onClick={handleRowClick}
        >
          {/* Expand Toggle */}
          <TableCell data-no-navigate>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="icon" className="h-6 w-6">
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </Button>
            </CollapsibleTrigger>
          </TableCell>

          {/* Order Info */}
          <TableCell>
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <span className="font-medium">{order.order_number}</span>
                <Badge
                  variant="outline"
                  className={cn("text-xs", ORDER_TYPE_COLORS[order.order_type as OrderType])}
                >
                  {order.order_type === "new_work" 
                    ? "🔨" 
                    : order.order_type === "field_work" 
                      ? "🔧" 
                      : "🔄"}
                </Badge>
                <Badge
                  variant="outline"
                  className={cn(
                    "text-xs",
                    FULFILLMENT_TYPE_COLORS[order.fulfillment_type as FulfillmentType]
                  )}
                >
                  {FULFILLMENT_TYPE_LABELS[order.fulfillment_type as FulfillmentType]}
                </Badge>
              </div>
              {configInfo?.isCustom ? (
                <div className="flex flex-col mt-1">
                  <span className="text-sm font-medium text-amber-700 dark:text-amber-500">
                    {configInfo.name}
                  </span>
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Wrench className="h-3 w-3" />
                    {configInfo.templateName}
                  </span>
                </div>
              ) : (
                productDesc && (
                  <span className="text-xs text-muted-foreground truncate max-w-[200px] mt-1">
                    {productDesc}
                  </span>
                )
              )}
            </div>
          </TableCell>

          {/* Customer */}
          <TableCell data-no-navigate>
            <div className="flex items-center gap-2">
              <span>{getCustomerDisplayName(order.customers)}</span>
              <ContactButtons
                phone={order.customers?.phone}
                email={order.customers?.email}
                customerName={getCustomerDisplayName(order.customers)}
                orderId={order.id}
                customerId={order.customers?.id}
                orderNumber={order.order_number}
              />
            </div>
          </TableCell>

          {/* Cemetery */}
          <TableCell>
            {order.cemeteries ? (
              <div className="flex flex-col">
                <span className="text-sm">{order.cemeteries.name}</span>
                <span className="text-xs text-muted-foreground">
                  {order.cemeteries.city}
                </span>
              </div>
            ) : (
              <span className="text-muted-foreground">-</span>
            )}
          </TableCell>

          {/* Partner */}
          <TableCell>
            {order.partners ? (
              <span className="text-sm">{order.partners.name}</span>
            ) : (
              <span className="text-muted-foreground">-</span>
            )}
          </TableCell>

          {/* Status */}
          <TableCell>
            <Badge className={ORDER_STATUS_COLORS[order.status as OrderStatus]}>
              {ORDER_STATUS_LABELS[order.status as OrderStatus]}
            </Badge>
          </TableCell>

          {/* Cemetery Approval */}
          <TableCell>
            {order.requires_cemetery_approval ? (
              <Badge
                variant="outline"
                className={cn(
                  "text-xs gap-1",
                  CEMETERY_APPROVAL_STATUS_COLORS[
                    order.cemetery_approval_status as CemeteryApprovalStatus
                  ] || CEMETERY_APPROVAL_STATUS_COLORS.pending
                )}
              >
                {order.cemetery_approval_status === "approved" && (
                  <CheckCircle2 className="h-3 w-3" />
                )}
                {order.cemetery_approval_status === "pending" && (
                  <Clock className="h-3 w-3" />
                )}
                {order.cemetery_approval_status === "rejected" && (
                  <XCircle className="h-3 w-3" />
                )}
                {!order.cemetery_approval_status && <Building2 className="h-3 w-3" />}
                {CEMETERY_APPROVAL_STATUS_LABELS[
                  order.cemetery_approval_status as CemeteryApprovalStatus
                ] || "Ei pyydetty"}
              </Badge>
            ) : (
              <span className="text-muted-foreground text-xs">-</span>
            )}
          </TableCell>

          {/* Age Badge */}
          <TableCell>
            <Badge variant="outline" className={cn("text-xs", daysBadge.colorClass)}>
              {daysBadge.days} pv
            </Badge>
          </TableCell>

          {/* Amount */}
          <TableCell className="text-right font-medium">
            {formatCurrency(order.total_amount)}
          </TableCell>

          {/* Actions */}
          <TableCell data-no-navigate>
            <DropdownMenu
              onOpenChange={(open) => {
                if (!open) {
                  dropdownJustClosed.current = true;
                  setTimeout(() => {
                    dropdownJustClosed.current = false;
                  }, 100);
                }
              }}
            >
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                onCloseAutoFocus={(e) => e.preventDefault()}
              >
                <DropdownMenuItem 
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/orders/${order.id}`);
                  }}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Avaa
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit();
                  }}
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  Muokkaa
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={(e) => {
                    e.stopPropagation();
                    onCopyLink();
                  }}
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Kopioi tilausnumero
                </DropdownMenuItem>
                {order.tracking_token && (
                  <DropdownMenuItem 
                    onClick={(e) => {
                      e.stopPropagation();
                      onCopyLink();
                    }}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Kopioi seurantalinkki
                  </DropdownMenuItem>
                )}
                {onStatusChange && STATUS_TRANSITIONS[order.status as string]?.map((nextStatus) => (
                  <DropdownMenuItem
                    key={nextStatus}
                    onClick={(e) => {
                      e.stopPropagation();
                      onStatusChange(order.id, nextStatus, order.status);
                    }}
                  >
                    <ChevronRight className="h-4 w-4 mr-2" />
                    → {ORDER_STATUS_LABELS[nextStatus]}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCancel();
                  }}
                  disabled={order.status === "cancelled"}
                >
                  <X className="h-4 w-4 mr-2" />
                  Peru tilaus
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </TableCell>
        </TableRow>

        {/* Expanded Content */}
        <CollapsibleContent asChild>
          <TableRow className="bg-muted/30 hover:bg-muted/30">
            <TableCell colSpan={10} className="p-4">
              <OrderExpandedDetails order={order} />
            </TableCell>
          </TableRow>
        </CollapsibleContent>
      </>
    </Collapsible>
  );
});
